import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Heart, Hourglass, HandHeart, Flame, Crown, Leaf } from "@phosphor-icons/react";
import ScrollFade from "./ScrollFade";
import { UserAvatar } from "./UserAvatar";
import { api } from "../lib/api";
import { ageOf } from "../lib/theme";

const days = (n) => `${n} day${n === 1 ? "" : "s"}`;

export default function Highlights({ version }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/summary/highlights").then((r) => setH(r.data)).catch(() => {}); }, [version]);
  if (!h) return null;
  const lead = h.top_caregivers?.[0];
  const chips = [
    { tint: "#30A14E", Icon: Leaf, text: <><b>{h.plant_count}</b> living plants · <b>{h.species_count}</b> species</>, testid: "hl-collection" },
    { tint: "#34C759", Icon: HandHeart, text: <><b>{h.care_this_week}</b> care logs this week{h.my_care_this_week ? <span className="text-muted-foreground"> · {h.my_care_this_week} by you</span> : null}</>, testid: "hl-week" },
    h.team_streak > 0 && { tint: "#FF9F0A", Icon: Flame, text: <><b>{days(h.team_streak)}</b> care streak</>, testid: "hl-streak" },
    lead && { tint: "#FFB800", Icon: Crown, avatar: lead, text: <>Top caregiver <b>{lead.name.split(" ")[0]}</b></>, testid: "hl-leader" },
    ...h.milestones.slice(0, 2).map((m) => ({ tint: "#E8A100", Icon: Trophy, text: <><b>{m.details?.title || "Milestone"}</b><span className="text-muted-foreground"> · {m.plant_name}</span></>, to: `/plants/${m.plant_id}?tab=growth`, testid: `hl-milestone-${m.id}` })),
    h.most_loved && { tint: "#FF5FA2", Icon: Heart, text: <>Most loved <b>{h.most_loved.nickname}</b></>, to: `/plants/${h.most_loved.id}`, testid: "hl-loved" },
    h.oldest && { tint: "#AF52DE", Icon: Hourglass, text: <>Elder <b>{h.oldest.nickname}</b><span className="text-muted-foreground"> · {ageOf(h.oldest.birth_date)}</span></>, to: `/plants/${h.oldest.id}`, testid: "hl-oldest" },
  ].filter(Boolean);
  return (
    <section className="mt-6" data-testid="highlights">
      <ScrollFade className="flex gap-2 py-1 px-0.5" testid="highlights-row">
        {chips.map((c, i) => <Chip key={c.testid} i={i} {...c} />)}
      </ScrollFade>
    </section>
  );
}

function Chip({ i, tint, Icon, text, to, testid, avatar }) {
  const cls = "shrink-0 h-9 pl-1.5 pr-3.5 rounded-full inline-flex items-center gap-2 text-[13px] whitespace-nowrap shadow-[0_1px_2px_rgba(0,0,0,0.05)] [&_b]:font-semibold";
  const inner = (
    <>
      {avatar ? <UserAvatar user={avatar} className="h-6 w-6 text-[9px]" /> : <span className="h-6 w-6 rounded-full inline-flex items-center justify-center" style={{ background: `${tint}22`, color: tint }}><Icon weight="fill" className="h-3.5 w-3.5" /></span>}
      <span>{text}</span>
    </>
  );
  const style = { background: `linear-gradient(160deg, ${tint}14, ${tint}05), var(--tile-base)` };
  const anim = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.04 * i, type: "spring", stiffness: 320, damping: 30 } };
  return to
    ? <motion.div {...anim} className="shrink-0"><Link to={to} className={`${cls} hover:brightness-[0.97] active:scale-[0.97] transition-[filter,transform]`} style={style} data-testid={testid}>{inner}</Link></motion.div>
    : <motion.span {...anim} className={cls} style={style} data-testid={testid}>{inner}</motion.span>;
}
